import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import FinanceCreditNotes from "@/pages/FinanceCreditNotes";
import { Redirect, Route, Switch } from "wouter";
import { useEffect, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { ROOT_REDIRECT_FLAG } from "./contexts/PortalModeContext";
import { homePathForUser } from "@shared/userRoles";
import { portalModuleAccessForUser } from "@shared/procurementInventory";
import ErrorBoundary from "./components/ErrorBoundary";
import { GlobalLoadingBar } from "./components/GlobalLoadingBar";
import { GlobalForegroundLoadingBridge } from "./components/GlobalForegroundLoadingBridge";
import MarketingRouteGuard from "./components/MarketingRouteGuard";
import HrsTesterRouteGuard from "./components/HrsTesterRouteGuard";
import KnowledgeCentreDomainRouter from "./components/knowledge-centre/KnowledgeCentreDomainRouter";
import ClarityOneDomainRouter from "./components/clarity-one/ClarityOneDomainRouter";
import ClarityOneAppShell from "./components/clarity-one/ClarityOneAppShell";
import ClarityWebsiteDomainRouter from "./components/clarity-website/ClarityWebsiteDomainRouter";
import ClarityWebsiteHeadphoneShopPage from "./pages/ClarityWebsiteHeadphoneShopPage";
import AdminHeadphoneOrdersPage from "./pages/AdminHeadphoneOrdersPage";
import ClarityReachoutPage from "./pages/ClarityReachoutPage";
import AdminReachoutPage from "./pages/AdminReachoutPage";
import { isKnowledgeCentreHostname } from "@shared/knowledgeCentreDomain";
import { isClarityWebsiteHostname } from "@shared/clarityWebsiteDomain";
import {
  isClarityOneMode,
  isClarityOneLocalDevEnabled,
  shouldShowClarityOneLocalDevFallback,
} from "@/lib/clarityOneMode";
import ClarityOneLocalDevFallback from "./pages/clarity-one/ClarityOneLocalDevFallback";
import CanvasPublicPage from "./pages/CanvasPublicPage";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PortalModeProvider } from "./contexts/PortalModeContext";
import { DaylightModeProvider } from "./contexts/DaylightModeContext";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import InviteAccept from "./pages/InviteAccept";
import Jobs from "./pages/Jobs";
import NewJob from "./pages/NewJob";
import JobDetail from "./pages/JobDetail";
import Customers from "./pages/Customers";
import CustomerIntelligencePage from "./pages/CustomerIntelligencePage";
import CustomerDetail from "./pages/CustomerDetail";
import WarrantyDraftPage from "./pages/WarrantyDraftPage";
import SiteVisits from "./pages/SiteVisits";
import NewSiteVisit from "./pages/NewSiteVisit";
import SiteVisitDetail from "./pages/SiteVisitDetail";
import TechnicianDashboard from "./pages/TechnicianDashboard";
import MyTasks from "./pages/MyTasks";
import JobTracker from "./pages/JobTracker";
import TrackSiteVisit from "./pages/TrackSiteVisit";
import Calendar from "./pages/Calendar";
import FieldCalendarPage from "./pages/FieldCalendarPage";
import { ServiceDashboardPage } from "@/components/service-dashboard/ServiceDashboardPage";
import {
  AdminAnalytics,
  RoServicingAnalytics,
  AdminArticleEditorPage,
  AdminArticlesPage,
  AdminChangelog,
  AdminCleoMismatches,
  AdminCleoUnknownSignals,
  AdminMcpConnections,
  AdminCoachingPlaybook,
  AdminCustomerCommunicationLogs,
  AdminCustomersPage,
  AdminActivityPage,
  AdminDigitalCardCompanyPage,
  AdminDuplicateProducts,
  AdminIssues,
  AdminMassDoUploadPage,
  AdminCleoOperationsDesk,
  AdminNotificationLogs,
  AdminNotifications,
  AdminProposalIntroPage,
  AdminProposalIntroPreviewPage,
  AdminSalesDisclaimers,
  AdminSettings,
  AdminUsers,
  ArticlePublicPage,
  ArticlesIndexPage,
  Broadcast,
  ClarityWebsiteExperiencePage,
  ClarityWebsiteAboutPage,
  ClarityWebsiteHogrPage,
  ClarityWebsiteHogrChapterPage,
  ClarityWebsiteSuperlativePage,
  ClarityWebsiteSuperlativeBrandPage,
  ClarityWebsiteSuperlativeProductPage,
  ClarityWebsiteBuildMyHomePage,
  ClarityWebsiteHomePage,
  CleoChatPage,
  CleoProjectProposalBetaPage,
  DealInvoicePage,
  DeliveryOrders,
  DigitalCardPreviewPage,
  DtoolsJsonReviewSandboxPage,
  DtoolsJsonSetupSandboxPage,
  DtoolsJsonUploadSandboxPage,
  DtoolsParserSandboxPage,
  DtoolsProposalIntegrityPage,
  FinanceDashboard,
  FinanceInvoices,
  FinanceLegacyInvoiceImport,
  FinanceReconciliation,
  FinanceRevenue,
  FinanceTasks,
  FollowUpStatusPage,
  HrsRackConfiguratorPage,
  Home,
  IntegrationTest,
  InventoryRequestCreatePage,
  InventoryRequestDetailPage,
  InvoicePage,
  KaleidescapeDashboardPage,
  ClubBookingsPage,
  ManualApprovedProposalWrapperPage,
  ManualUploadedProposalDetailPage,
  MyDigitalCardPage,
  MyProposals,
  PICDashboard,
  PortalInvoicePage,
  PortalInvoiceProposalPage,
  ProcurementDashboardPage,
  ProcurementDetailPage,
  ProcurementGuidePage,
  ProcurementListPage,
  ProcurementMurhoDocumentsPage,
  ProcurementCorrespondenceInboxPage,
  AdminProcurementCorrespondencePage,
  ProcurementPickListPage,
  ProductDetail,
  Products,
  ProjectCatalogPage,
  ProjectCatalogueSandboxPage,
  ProjectDetailPage,
  ProjectMarkupPage,
  ProjectMarkupSandboxPage,
  ProjectProposalPublicPage,
  ProjectProposalStaffPreviewPage,
  ProjectSharePublicPage,
  ProjectsListPage,
  ProposalApprovalSuccessPage,
  ProposalIntelligenceSandboxPage,
  ProposalLinkBUnavailablePage,
  ProposalPublicPage,
  ProposalWrapperPage,
  ProposalsAwaitingInvoice,
  SecureProposalPublicPage,
  PublicDigitalCardPage,
  RetailTermsPage,
  RevenueCalendar,
  RevenueSchedule,
  ReviseProposalPage as ReviseProposalPageRaw,
  VariationOrderWorkspacePage as VariationOrderWorkspacePageRaw,
  VariationOrderPublicPage,
  VariationOrderStaffPreviewPage,
  RODeliveryPage,
  ROInvoicePage,
  SalesCataloguePage,
  SalesCustomerLogPage,
  SalesAppointmentsPage,
  SalesDailyBriefingPage,
  SalesDashboard,
  SalesDealOverviewPage,
  ProjectBomPreviewPage,
  SalesInsightsPage,
  SalesInsightsSandboxPage,
  SalesModulePlaceholderPage,
  SalesProjectCardPage,
  SalesProjectsPage,
  SalesProposalIdEntryPage,
  SalesSandboxPage,
  SalesSettings,
  StaffPreferencesPage,
  Suppliers,
  SvrAnalytics,
  TimeMachinePage,
} from "./app/lazyRoutes";
import { withProposalMutationRouteGuard } from "@/components/sales/ProposalMutationRouteGuard";

const ReviseProposalPage = withProposalMutationRouteGuard(
  ReviseProposalPageRaw,
  {
    title: "Read-only access",
    description:
      "Your Inventory / Procurement role can view proposals but cannot revise them.",
  }
);
const VariationOrderWorkspacePage = withProposalMutationRouteGuard(
  VariationOrderWorkspacePageRaw,
  {
    title: "Read-only access",
    description:
      "Your Inventory / Procurement role can view proposals but cannot create or edit Variation Orders.",
  }
);

const ROOT_REDIRECT_HOME_KEYS = ["/sales", "/procurement"];

function PortalRootRedirect() {
  const { user, loading } = useAuth();
  const [decided, setDecided] = useState(false);
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (decided || loading || !user) return;
    setDecided(true);
    let alreadyApplied = false;
    try {
      alreadyApplied = Boolean(sessionStorage.getItem(ROOT_REDIRECT_FLAG));
      sessionStorage.setItem(ROOT_REDIRECT_FLAG, "1");
    } catch {
      alreadyApplied = true;
    }
    if (alreadyApplied) return;
    const access = portalModuleAccessForUser(user);
    const home = homePathForUser(user, m => access[m]);
    if (home && ROOT_REDIRECT_HOME_KEYS.includes(home)) {
      setTarget(home);
    }
  }, [user, loading, decided]);

  if (target) return <Redirect to={target} />;
  return <ServiceDashboardPage />;
}

function Router() {
  if (
    typeof window !== "undefined" &&
    isKnowledgeCentreHostname(window.location.hostname)
  ) {
    return <KnowledgeCentreDomainRouter />;
  }

  // Future public website — inert until DNS points www.claritygroup.com.sg here.
  if (
    typeof window !== "undefined" &&
    isClarityWebsiteHostname(window.location.hostname)
  ) {
    return <ClarityWebsiteDomainRouter />;
  }

  if (typeof window !== "undefined") {
    if (
      shouldShowClarityOneLocalDevFallback(
        window.location.hostname,
        window.location.pathname
      )
    ) {
      return <ClarityOneLocalDevFallback />;
    }
  }

  if (isClarityOneMode()) {
    return <ClarityOneDomainRouter />;
  }

  return (
    <Switch>
      <Route path="/repair-orders" component={Jobs} />
      <Route path="/repair-orders/new" component={NewJob} />
      <Route path="/repair-orders/id/:id" component={JobDetail} />
      <Route path="/jobs" component={Jobs} />
      <Route path="/jobs/new" component={NewJob} />
      <Route path="/jobs/:id" component={JobDetail} />
      <Route path="/customers" component={Customers} />
      <Route
        path="/customers/intelligence"
        component={CustomerIntelligencePage}
      />
      <Route path="/customers/id/:id" component={CustomerDetail} />
      <Route path="/customers/:id" component={CustomerDetail} />
      <Route path="/projects/:projectId/markup" component={ProjectMarkupPage} />
      <Route path="/projects/:projectId" component={ProjectDetailPage} />
      <Route path="/projects" component={ProjectsListPage} />
      <Route path="/products" component={Products} />
      <Route path="/products/:id" component={ProductDetail} />
      <Route path="/warranties/:id" component={WarrantyDraftPage} />
      <Route path="/delivery-orders" component={DeliveryOrders} />
      <Route path="/settings" component={AdminSettings} />
      <Route path="/changelog" component={AdminChangelog} />
      <Route
        path="/admin/procurement-correspondence"
        component={AdminProcurementCorrespondencePage}
      />
      <Route
        path="/procurement/mass-do-upload"
        component={AdminMassDoUploadPage}
      />
      <Route
        path="/admin/procurement-mass-do-upload"
        component={AdminMassDoUploadPage}
      />
      <Route path="/admin" component={AdminUsers} />
      <Route path="/admin/headphone-orders" component={AdminHeadphoneOrdersPage} />
      <Route path="/shopheadphones" component={ClarityWebsiteHeadphoneShopPage} />
      <Route path="/reachout" component={ClarityReachoutPage} />
      <Route path="/admin/reachout" component={AdminReachoutPage} />
      <Route path="/admin/customers" component={AdminCustomersPage} />
      <Route path="/admin/activity" component={AdminActivityPage} />
      <Route path="/sales/activity" component={AdminActivityPage} />
      <Route
        path="/admin/digital-card-company"
        component={AdminDigitalCardCompanyPage}
      />
      <Route path="/admin/kaleidescape" component={KaleidescapeDashboardPage} />
      <Route path="/club-bookings" component={ClubBookingsPage} />
      <Route path="/admin/upload-price-list" component={AdminSettings} />
      <Route path="/site-visits" component={SiteVisits} />
      <Route path="/site-visits/new" component={NewSiteVisit} />
      <Route path="/site-visits/id/:id" component={SiteVisitDetail} />
      <Route path="/site-visits/:id" component={SiteVisitDetail} />
      <Route path="/my-digital-card" component={MyDigitalCardPage} />
      <Route
        path="/my-digital-card/preview"
        component={DigitalCardPreviewPage}
      />
      <Route path="/my-tasks" component={MyTasks} />
      <Route path="/my-schedule" component={TechnicianDashboard} />
      <Route path="/review-queue" component={PICDashboard} />
      <Route path="/billing-queue" component={FinanceDashboard} />
      <Route path="/notifications" component={AdminNotifications} />
      <Route
        path="/admin/notification-logs"
        component={AdminNotificationLogs}
      />
      <Route
        path="/admin/customer-communication-logs"
        component={AdminCustomerCommunicationLogs}
      />
      <Route
        path="/admin/duplicate-products"
        component={AdminDuplicateProducts}
      />
      <Route
        path="/admin/sales-disclaimers"
        component={AdminSalesDisclaimers}
      />
      <Route
        path="/admin/proposals/intro/preview"
        component={AdminProposalIntroPreviewPage}
      />
      <Route path="/admin/proposals/intro" component={AdminProposalIntroPage} />
      <Route path="/admin/articles/new" component={AdminArticleEditorPage} />
      <Route path="/admin/articles/:id" component={AdminArticleEditorPage} />
      <Route path="/admin/articles" component={AdminArticlesPage} />
      <Route path="/admin/analytics" component={AdminAnalytics} />
      <Route path="/admin/ro-servicing" component={RoServicingAnalytics} />
      <Route path="/admin/svr-analytics" component={SvrAnalytics} />
      <Route path="/home" component={Home} />
      <Route path="/login" component={Login} />
      {isClarityOneLocalDevEnabled() ? (
        <Route path="/staff-login" component={Login} />
      ) : null}
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/invite/:token" component={InviteAccept} />
      <Route path="/track/:token" component={JobTracker} />
      <Route path="/track/svr/:token" component={TrackSiteVisit} />
      <Route path="/invoice/:token" component={InvoicePage} />
      <Route path="/ro-invoice/:token" component={ROInvoicePage} />
      <Route path="/ro-delivery/:token" component={RODeliveryPage} />
      <Route path="/deal-invoice/:token" component={DealInvoicePage} />
      <Route path="/portal-invoice/:token" component={PortalInvoicePage} />
      <Route
        path="/portal-invoice/:token/proposal"
        component={PortalInvoiceProposalPage}
      />
      {/* Secure Link B — the sole public proposal authorization credential. */}
      <Route
        path="/p/:readableRef/:sequence/:token/approved"
        component={ProposalApprovalSuccessPage}
      />
      <Route
        path="/p/:readableRef/:sequence/:token"
        component={SecureProposalPublicPage}
      />
      {/*
        Legacy numeric proposal links never redirect to a secure Link B URL (that would let a
        caller enumerate proposals by trying ids) — they always show the generic unavailable
        message. Staff still use these exact paths from authenticated internal navigation; the
        pages themselves gate on staff role and render ProposalLinkBUnavailablePage otherwise.
      */}
      <Route
        path="/proposal/:proposalId/approved"
        component={ProposalLinkBUnavailablePage}
      />
      <Route
        path="/proposal/:proposalId"
        component={ProposalLinkBUnavailablePage}
      />
      <Route path="/canvas/krtw/:token" component={CanvasPublicPage} />
      <Route path="/canvas/krtw" component={CanvasPublicPage} />
      <Route path="/canvas/:token" component={CanvasPublicPage} />
      <Route path="/canvas" component={CanvasPublicPage} />
      <Route path="/vo/:token" component={VariationOrderPublicPage} />
      <Route
        path="/proposal-manual/:proposalId"
        component={ProposalLinkBUnavailablePage}
      />
      <Route
        path="/project/:slug/c/:categoryId/o/:optionId"
        component={ProjectProposalPublicPage}
      />
      <Route
        path="/project/:slug/c/:categoryId"
        component={ProjectProposalPublicPage}
      />
      <Route path="/project/:slug" component={ProjectProposalPublicPage} />
      <Route
        path="/proposal-wrapper/:projectId/:revisionId"
        component={ProposalWrapperPage}
      />
      <Route path="/u/:slug" component={PublicDigitalCardPage} />
      <Route path="/share/:token" component={ProjectSharePublicPage} />
      <Route path="/articles/:slug" component={ArticlePublicPage} />
      <Route path="/articles" component={ArticlesIndexPage} />
      <Route path="/terms/retail" component={RetailTermsPage} />
      <Route path="/calendar/field" component={FieldCalendarPage} />
      <Route path="/calendar" component={Calendar} />
      <Route path="/catalog">
        <Redirect to="/sales/catalogue" />
      </Route>
      <Route path="/catalogue">
        <Redirect to="/sales/catalogue" />
      </Route>
      <Route path="/suppliers" component={Suppliers} />
      <Route path="/admin/integration" component={IntegrationTest} />
      <Route path="/finance/reconciliation" component={FinanceReconciliation} />
      <Route path="/finance/tasks" component={FinanceTasks} />
      <Route path="/finance/revenue" component={FinanceRevenue} />
      <Route path="/finance/calendar" component={RevenueCalendar} />
      <Route path="/finance/invoices" component={FinanceInvoices} />
      <Route path="/finance/credit-notes" component={FinanceCreditNotes} />
      <Route
        path="/finance/legacy-import"
        component={FinanceLegacyInvoiceImport}
      />
      <Route
        path="/proposals-awaiting-invoice"
        component={ProposalsAwaitingInvoice}
      />
      <Route path="/sales/briefing" component={SalesDailyBriefingPage} />
      <Route path="/sales/insights" component={SalesInsightsPage} />
      <Route
        path="/sales/projects/:id/cleo-proposal"
        component={CleoProjectProposalBetaPage}
      />
      <Route
        path="/sales/projects/:projectId/interactive-preview/c/:categoryId/o/:optionId"
        component={ProjectProposalStaffPreviewPage}
      />
      <Route
        path="/sales/projects/:projectId/interactive-preview/c/:categoryId"
        component={ProjectProposalStaffPreviewPage}
      />
      <Route
        path="/sales/projects/:projectId/interactive-preview"
        component={ProjectProposalStaffPreviewPage}
      />
      <Route
        path="/sales/projects/:projectId"
        component={SalesProjectCardPage}
      />
      <Route path="/sales/projects" component={SalesProjectsPage} />
      <Route path="/sales/walk-in">
        <Redirect to="/sales/customer-log" />
      </Route>
      <Route path="/sales/customer-log" component={SalesCustomerLogPage} />
      <Route path="/sales/appointments" component={SalesAppointmentsPage} />
      <Route path="/sales/customers/:id" component={CustomerDetail} />
      <Route path="/sales/catalogue" component={SalesCataloguePage} />
      <Route path="/sales/project-catalog" component={ProjectCatalogPage} />
      <Route path="/sales/settings" component={SalesSettings} />
      <Route
        path="/sales/follow-up/:reminderId"
        component={FollowUpStatusPage}
      />
      <Route
        path="/sales/proposals/:proposalId/preview-manual"
        component={ManualApprovedProposalWrapperPage}
      />
      <Route
        path="/sales/proposals/:proposalId/preview"
        component={ProposalPublicPage}
      />
      <Route
        path="/sales/proposals/:id/revise"
        component={ReviseProposalPage}
      />
      <Route
        path="/sales/proposals/:id/vo/new"
        component={VariationOrderWorkspacePage}
      />
      <Route
        path="/sales/proposals/:id/vo/:voId"
        component={VariationOrderWorkspacePage}
      />
      <Route
        path="/sales/vo/:voId/preview"
        component={VariationOrderStaffPreviewPage}
      />
      <Route
        path="/sales/proposals/:id/dtools-integrity"
        component={DtoolsProposalIntegrityPage}
      />
      <Route
        path="/sales/proposals/:id/manual"
        component={ManualUploadedProposalDetailPage}
      />
      <Route
        path="/sales/proposals/:id/deal"
        component={SalesDealOverviewPage}
      />
      <Route
        path="/sales/proposals/:id/bill-of-materials"
        component={ProjectBomPreviewPage}
      />
      <Route path="/sales/proposals/:id" component={SalesProposalIdEntryPage} />
      <Route path="/sales/my-proposals">
        <Redirect to="/sales/proposals" />
      </Route>
      <Route path="/sales/proposals" component={MyProposals} />
      <Route path="/sales/invoices" component={FinanceInvoices} />
      <Route
        path="/sales/sandbox/dtools-parser"
        component={DtoolsParserSandboxPage}
      />
      {/* Flat routes only — nested wouter `nest` rewrites absolute sidebar/nav paths
          (e.g. /sales → /sales/dtools-json-upload/sales) and leaves a blank outlet. */}
      <Route
        path="/sales/dtools-json-upload/review/:sessionId"
        component={DtoolsJsonReviewSandboxPage}
      />
      <Route
        path="/sales/dtools-json-upload/setup/:sessionId"
        component={DtoolsJsonSetupSandboxPage}
      />
      <Route
        path="/sales/dtools-json-upload"
        component={DtoolsJsonUploadSandboxPage}
      />
      <Route
        path="/sales/sandbox/dtools-json-upload/review/:sessionId"
        component={DtoolsJsonReviewSandboxPage}
      />
      <Route
        path="/sales/sandbox/dtools-json-upload/setup/:sessionId"
        component={DtoolsJsonSetupSandboxPage}
      />
      <Route
        path="/sales/sandbox/dtools-json-upload"
        component={DtoolsJsonUploadSandboxPage}
      />
      <Route
        path="/sales/sandbox/proposal-intelligence"
        component={ProposalIntelligenceSandboxPage}
      />
      <Route
        path="/sales/sandbox/sales-insights"
        component={SalesInsightsSandboxPage}
      />
      <Route
        path="/sales/sandbox/project-markup"
        component={ProjectMarkupSandboxPage}
      />
      <Route
        path="/sales/sandbox/project-catalogue"
        component={ProjectCatalogueSandboxPage}
      />
      <Route
        path="/sales/sandbox/hrs-rack-configurator"
        component={HrsRackConfiguratorPage}
      />
      <Route path="/sales/time-machine" component={TimeMachinePage} />
      <Route path="/sales/sandbox" component={SalesSandboxPage} />
      <Route
        path="/sandbox/clarity-website/superlative-audio-systems/brands/:brand"
        component={ClarityWebsiteSuperlativeBrandPage}
      />
      <Route
        path="/sandbox/clarity-website/superlative-audio-systems/products/:slug"
        component={ClarityWebsiteSuperlativeProductPage}
      />
      <Route
        path="/sandbox/clarity-website/superlative-audio-systems"
        component={ClarityWebsiteSuperlativePage}
      />
      <Route
        path="/sandbox/clarity-website/experiences/:slug"
        component={ClarityWebsiteExperiencePage}
      />
      <Route
        path="/sandbox/clarity-website/about"
        component={ClarityWebsiteAboutPage}
      />
      <Route
        path="/sandbox/clarity-website/build-a-project/my-home"
        component={ClarityWebsiteBuildMyHomePage}
      />
      <Route
        path="/sandbox/clarity-website/build-a-project/:chapter"
        component={ClarityWebsiteHogrChapterPage}
      />
      <Route
        path="/sandbox/clarity-website/build-a-project"
        component={ClarityWebsiteHogrPage}
      />
      <Route
        path="/sandbox/clarity-website"
        component={ClarityWebsiteHomePage}
      />
      <Route path="/sandbox/dtools-json-upload">
        <Redirect to="/sales/sandbox/dtools-json-upload" />
      </Route>
      <Route path="/sandbox/proposal-intelligence">
        <Redirect to="/sales/sandbox/proposal-intelligence" />
      </Route>
      <Route path="/sandbox/sales-insights">
        <Redirect to="/sales/sandbox/sales-insights" />
      </Route>
      <Route path="/sandbox/project-markup">
        <Redirect to="/sales/sandbox/project-markup" />
      </Route>
      <Route path="/sandbox/project-catalogue">
        <Redirect to="/sales/sandbox/project-catalogue" />
      </Route>
      <Route path="/sandbox">
        <Redirect to="/sales/sandbox" />
      </Route>
      <Route path="/sales" component={SalesDashboard} />
      <Route path="/finance/schedule" component={RevenueSchedule} />
      <Route path="/broadcast" component={Broadcast} />
      <Route path="/broadcast/:id" component={Broadcast} />
      <Route path="/admin/issues" component={AdminIssues} />
      <Route path="/cleo/operations" component={AdminCleoOperationsDesk} />
      <Route
        path="/cleo/operations/:requestKey"
        component={AdminCleoOperationsDesk}
      />
      <Route path="/cleo" component={CleoChatPage} />
      <Route
        path="/admin/cleo-operations/:requestKey"
        component={AdminCleoOperationsDesk}
      />
      <Route
        path="/admin/cleo-operations"
        component={AdminCleoOperationsDesk}
      />
      <Route path="/admin/cleo-mismatches" component={AdminCleoMismatches} />
      <Route path="/admin/mcp-connections" component={AdminMcpConnections} />
      <Route path="/admin/coaching-playbook" component={AdminCoachingPlaybook} />
      <Route
        path="/admin/cleo-unknown-signals"
        component={AdminCleoUnknownSignals}
      />
      <Route path="/my-preferences" component={StaffPreferencesPage} />
      <Route path="/procurement/guide" component={ProcurementGuidePage} />
      {/* Legacy Retail Handovers — redirect shells into the unified workspace */}
      <Route
        path="/procurement/handovers/:id"
        component={ProcurementDetailPage}
      />
      <Route path="/procurement/handovers" component={ProcurementListPage} />
      <Route
        path="/procurement/inventory-requests/new"
        component={InventoryRequestCreatePage}
      />
      <Route
        path="/procurement/inventory-requests/:id"
        component={InventoryRequestDetailPage}
      />
      <Route
        path="/procurement/correspondence"
        component={ProcurementCorrespondenceInboxPage}
      />
      <Route
        path="/procurement/murho-documents"
        component={ProcurementMurhoDocumentsPage}
      />
      <Route
        path="/procurement/:id/pick-list"
        component={ProcurementPickListPage}
      />
      <Route path="/procurement" component={ProcurementDashboardPage} />
      <Route path="/dashboard" component={ServiceDashboardPage} />
      <Route path="/" component={PortalRootRedirect} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  if (typeof window !== "undefined" && isClarityOneMode()) {
    return (
      <ErrorBoundary>
        <ThemeProvider defaultTheme="light">
          <TooltipProvider>
            <ClarityOneAppShell>
              <Router />
            </ClarityOneAppShell>
          </TooltipProvider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <PortalModeProvider>
            <DaylightModeProvider>
              <GlobalForegroundLoadingBridge />
              <GlobalLoadingBar />
              <Toaster />
              <MarketingRouteGuard>
                <HrsTesterRouteGuard>
                  <Router />
                </HrsTesterRouteGuard>
              </MarketingRouteGuard>
            </DaylightModeProvider>
          </PortalModeProvider>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
